import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Stack,
  InputAdornment,
} from "@mui/material";
import { FormikProps } from "formik";
import { TalentProfile } from "../../../types/user";
import { StageCardFormValues } from "@/types/Card";
import ImageUpload from "../../../components/common/ImageUpload";
import { useSnackbar } from "notistack";
import { Icons } from "../../../components/assets/icon";
import CustomTextField from "../../../components/common/CustomTextField";

const LabelBlock = ({
  label,
  required = false,
  helper,
}: {
  label: string;
  required?: boolean;
  helper?: string;
}) => (
  <Box>
    <Typography
      sx={{ font: "var(--semibold-md)", color: "var(--neutral-1000)", mb: 0.5 }}
    >
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </Typography>
    {helper && (
      <Typography sx={{ font: "var(--body-sm)", color: "var(--neutral-500)" }}>
        {helper}
      </Typography>
    )}
  </Box>
);

type Props = {
  formik: FormikProps<StageCardFormValues>;
  currentProfile: TalentProfile;
  onNext: () => void;
  onBack: () => void;
};

export default function StepThree_StageMedia({
  formik,
  currentProfile,
  onNext,
  onBack,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [videoUrl, setVideoUrl] = useState(formik.values.previewVideoUrl || "");
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const thumbnail = formik.values.primaryThumbnail;
    if (thumbnail instanceof File)
      setThumbnailPreview(URL.createObjectURL(thumbnail));
    else if (typeof thumbnail === "string") setThumbnailPreview(thumbnail);

    const gallery = formik.values.galleryImages;
    if (Array.isArray(gallery)) {
      const previews = (gallery as (string | File)[]).map((item) =>
        item instanceof File ? URL.createObjectURL(item) : item
      );
      setGalleryPreviews(previews);
    }
  }, [formik.values.primaryThumbnail, formik.values.galleryImages]);

  const handleThumbnailChange = (files: FileList | null) => {
    if (files?.[0]) {
      const file = files[0];
      formik.setFieldValue("primaryThumbnail", file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryChange = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      // Get existing gallery images
      const existingGallery = formik.values.galleryImages || [];
      // Combine existing and new files
      const updatedGallery = [...existingGallery, ...fileArray];
      formik.setFieldValue("galleryImages", updatedGallery);
      // Update previews by combining existing and new previews
      const newPreviews = fileArray.map((f) => URL.createObjectURL(f));
      setGalleryPreviews([...galleryPreviews, ...newPreviews]);
    }
  };

  const handleNext = async () => {
    setIsSubmitting(true);
    const errors = await formik.validateForm();

    const stepTwoFields = [
      "primaryThumbnail",
      "galleryImages",
      "previewVideoUrl",
    ];

    const stepTwoErrors = Object.keys(errors).filter((key) =>
      stepTwoFields.includes(key)
    );

    if (stepTwoErrors.length === 0) {
      onNext();
    } else {
      const touchedFields = stepTwoFields.reduce(
        (acc, field) => {
          acc[field] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );

      formik.setTouched(touchedFields, true);
      const firstError = Object.values(errors)[0];
      if (firstError && typeof firstError === "string") {
        enqueueSnackbar(firstError, { variant: "error" });
      }
      console.error("❌ Step 2 Validation Errors:", errors);
    }
    setIsSubmitting(false);
  };

  return (
    <Box>
      <Typography
        variant="h5"
        mb={3}
        fontFamily="Lexend"
        fontWeight={700}
        textAlign="start"
        sx={{ color: "#333", pt: 3, pb: 2 }}
      >
        Media Upload & Session Type
      </Typography>

      <Grid container spacing={4}>
        {/* Thumbnail Upload */}
        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Primary Thumbnail"
            helper="Draw in your perfect clients by displaying visual examples of what you provide."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ImageUpload onFileSelect={handleThumbnailChange} layout="row" />
          {thumbnailPreview && (
            <Box mt={2}>
              <Box
                component="img"
                src={thumbnailPreview}
                alt="Thumbnail Preview"
                sx={{
                  width: 180,
                  height: 120,
                  objectFit: "cover",
                  mt: 1,
                  borderRadius: 2,
                }}
              />
            </Box>
          )}
        </Grid>

        {/* Video URL */}
        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Video Preview (YouTube/Vimeo URL)"
            helper="Grab the attention of potential buyers by creating a video that highlights your service."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            placeholder="https://youtube.com/..."
            value={videoUrl}
            onChange={(e) => {
              setVideoUrl(e.target.value);
              formik.setFieldValue("previewVideoUrl", e.target.value);
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icons.Globe height={20} width={20} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>

        {/* Gallery Upload */}
        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Gallery Images"
            helper="Upload eye-catching visuals of your services in the Gallery section for potential clients to see your talent."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ImageUpload
            onFileSelect={handleGalleryChange}
            multiple
            name="gallery_images"
            layout="row"
          />
          {galleryPreviews.length > 0 && (
            <Stack direction="row" spacing={2} mt={2} flexWrap="wrap">
              {galleryPreviews.map((src, idx) => (
                <Box
                  key={idx}
                  component="img"
                  src={src}
                  alt={`Gallery ${idx}`}
                  sx={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 2,
                  }}
                />
              ))}
            </Stack>
          )}
        </Grid>

        {/* Navigation */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button variant="outlined" onClick={onBack}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                fontFamily: "Lexend",
                fontWeight: 600,
                background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                px: 4,
                py: 1.5,
              }}
              disabled={isSubmitting}
            >
              Next
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
